// src/Components/Vendor103/index.js
import React, { useEffect, useState } from "react";
import axios from "axios";

const Vendor103 = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const getParam = (key) => params.get(key) || "";

    // Initial extraction
    let finalFname = getParam("fname").trim();
    let finalLname = getParam("lname").trim();
    let finalEmail = getParam("email").trim();
    let finalPhone = getParam("phone").trim();
    let finalCity = getParam("city").trim();
    let finalState = getParam("state").trim();
    let finalZip = getParam("zip").trim();
    let finalStreet = getParam("street").trim();

    const brand = getParam("brand") || "default";
    const aff_sub = getParam("aff_sub") || "";
    const aff_click_id = getParam("aff_click_id") || "";

    // ---------- Validation helpers ----------
    const isValidEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
    const formatPhone = (ph) => {
      const digits = ph.replace(/\D/g, "");
      return digits.length >= 10 ? digits.slice(-10) : "";
    };
    const isValidState = (st) => /^[A-Z]{2}$/.test(st);
    const isValidZip = (zp) => /^\d{5}$/.test(zp);

    // Format phone if present
    if (finalPhone) finalPhone = formatPhone(finalPhone);

    // ---------- Build routing URLs ----------
    const routingUrls = {
      tho: {
        1: [
          "https://go.nationalamericanproperties.com/aff_ad?campaign_id=302&aff_id=276&hostNameId=23124&source=aff-cc",
        ],
        2: [
          "https://go.nationalamericanproperties.com/aff_ad?campaign_id=305&aff_id=276&hostNameId=23124&source=aff-cc",
        ],
        3: [
          "https://go.nationalamericanproperties.com/aff_ad?campaign_id=308&aff_id=276&hostNameId=23124&source=aff-cc",
        ],
      },
      default: {
        1: [
          "https://go.nationalamericanproperties.com/aff_ad?campaign_id=303&aff_id=276&hostNameId=23124&source=aff-cc",
        ],
        2: [
          "https://go.nationalamericanproperties.com/aff_ad?campaign_id=306&aff_id=276&hostNameId=23124&source=aff-cc",
        ],
        3: [
          "https://go.nationalamericanproperties.com/aff_ad?campaign_id=309&aff_id=276&hostNameId=23124&source=aff-cc",
        ],
      },
    };

    // ---------- Helper to replace placeholders ----------
    const buildRedirectUrl = (base) => {
      return `${base}&aff_sub=${encodeURIComponent(
        aff_sub
      )}&aff_click_id=${encodeURIComponent(
        aff_click_id
      )}&aff_unique2=${encodeURIComponent(
        finalFname
      )}&aff_sub3=${encodeURIComponent(
        finalLname
      )}&aff_unique5=${encodeURIComponent(
        finalStreet
      )}&aff_sub4=${encodeURIComponent(
        finalCity
      )}&aff_sub5=${encodeURIComponent(
        finalState
      )}&aff_unique1=${encodeURIComponent(
        finalZip
      )}&aff_unique3=${encodeURIComponent(
        finalEmail
      )}&aff_unique4=${encodeURIComponent(finalPhone)}`;
    };

    // ---------- Partial match API call ----------
    const fetchPartialMatchIfNeeded = async () => {
      try {
        const missingOrInvalid =
          !finalFname ||
          !finalLname ||
          !isValidEmail(finalEmail) ||
          !finalPhone ||
          !finalStreet ||
          !finalCity ||
          !isValidState(finalState) ||
          !isValidZip(finalZip);

        if (!missingOrInvalid) return;

        const queryParams = [];

        const constructedName =
          finalFname && finalLname
            ? `${finalFname} ${finalLname}`
            : finalFname || finalLname;

        if (constructedName)
          queryParams.push(`name=${encodeURIComponent(constructedName)}`);
        if (isValidEmail(finalEmail))
          queryParams.push(`email=${encodeURIComponent(finalEmail)}`);
        if (finalPhone)
          queryParams.push(`phone=${encodeURIComponent(finalPhone)}`);

        const constructedAddress = [
          finalStreet,
          finalCity,
          finalState,
          finalZip,
        ]
          .filter(Boolean)
          .join(", ");
        if (constructedAddress)
          queryParams.push(`address=${encodeURIComponent(constructedAddress)}`);

        if (queryParams.length === 0) return;

        const queryString = queryParams.join("&");
        const response = await axios.get(
          `https://api.palisade.ai/checkout/v3/search/partial-match?${queryString}`
        );
        const source = response?.data?.[0]?._source;
        console.log("Partial Match API Response:", source);

        if (source) {
          if (source?.name) {
            const parts = source.name.trim().split(" ");
            if (!finalFname && parts.length) finalFname = parts[0];
            if (!finalLname && parts.length > 1)
              finalLname = parts.slice(1).join(" ");
          }

          if (!isValidEmail(finalEmail) && source?.email) {
            finalEmail = source.email;
          }
          if (!finalPhone && source?.phone) {
            finalPhone = formatPhone(source.phone);
          }
          if (
            !finalStreet ||
            !finalCity ||
            !isValidState(finalState) ||
            !isValidZip(finalZip)
          ) {
            const addrParts = (source?.address || "")
              .split(",")
              .map((p) => p.trim());
            if (!finalStreet) finalStreet = addrParts[0] || "";
            if (!finalCity) finalCity = addrParts[1] || "";
            if (!finalState || !isValidState(finalState))
              finalState = addrParts[2] || "";
            if (!finalZip || !isValidZip(finalZip))
              finalZip = addrParts[3] || "";
          }
        }
      } catch (err) {
        console.error("Partial Match API Error:", err);
      }
    };

    // ---------- Main routing ----------
    const routeUser = async () => {
      await fetchPartialMatchIfNeeded();

      // Debug log before redirect
      console.log("🔍 Final merged redirect values:", {
        fname: finalFname,
        lname: finalLname,
        email: finalEmail,
        phone: finalPhone,
        street: finalStreet,
        city: finalCity,
        state: finalState,
        zip: finalZip,
      });

      const eligiblePriority1 =
        isValidEmail(finalEmail) &&
        finalPhone &&
        finalStreet &&
        finalCity &&
        isValidState(finalState) &&
        isValidZip(finalZip);

      let priority = 3;
      if (eligiblePriority1) priority = 1;
      else if (
        finalEmail ||
        finalPhone ||
        finalCity ||
        finalState ||
        finalZip ||
        finalStreet
      )
        priority = 2;

      const brandKey = brand === "tho" ? "tho" : "default";
      const urls = routingUrls[brandKey][priority];
      const redirectUrl = buildRedirectUrl(urls[0]);

      console.log("➡️ Redirecting to:", redirectUrl);

      window.location.href = redirectUrl;
    };

    routeUser().finally(() => setLoading(false));
  }, []);

  return <div>{loading ? "Loading..." : null}</div>;
};

export default Vendor103;
